import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslocoModule } from '@jsverse/transloco';
import { TextInputComponent } from '../text-input/text-input.component';
import { DateInputComponent } from '../date-input/date-input.component';

type InputDef = {
  formControlName: string;
  require?: boolean;
  type?: 'input' | 'textbox' | 'date' | 'checkbox' | 'select';
  hint?: string;
};

type GroupDef = { groupName: string; inputs: InputDef[] };

type DynamicDefinition = {
  name?: string;
  inputs?: InputDef[];
  groups?: GroupDef[];
};

@Component({
  selector: 'anihis-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    TranslocoModule,
    TextInputComponent,
    DateInputComponent,
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() definition!: DynamicDefinition;
  @Input() formGroup?: FormGroup;

  @Output() formCreated = new EventEmitter<FormGroup>();
  @Output() formChange = new EventEmitter<any>();

  private fb = new FormBuilder();
  group!: FormGroup;

  ready = false;

  ngOnInit(): void {
    this.group = this.formGroup ?? this.buildGroup(this.definition);
    // emit created form so parent can hook into it
    this.formCreated.emit(this.group);

    this.group.valueChanges.subscribe((v) => this.formChange.emit(v));
    // If no external formGroup was provided we'll be ready now; otherwise wait for ngOnChanges to ensure controls
    if (!this.formGroup) {
      this.ready = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formGroup'] && this.formGroup) {
      // use provided group and ensure it contains necessary controls
      this.group = this.formGroup;
      this.ensureControls(this.group, this.definition);
      this.ready = true;
    }

    if (changes['definition'] && !this.formGroup) {
      // rebuild group from new definition when parent didn't pass a formGroup
      this.group = this.buildGroup(this.definition);
      this.ready = true;
    }
  }

  private buildGroup(def: DynamicDefinition): FormGroup {
    const g: any = {};
    if (def.groups && def.groups.length) {
      def.groups.forEach((gr) => {
        const controls: any = {};
        gr.inputs.forEach((input) => {
          controls[input.formControlName] = [
            '',
            input.require ? Validators.required : [],
          ];
        });
        g[gr.groupName] = this.fb.group(controls);
      });
    } else if (def.inputs) {
      def.inputs.forEach((input) => {
        g[input.formControlName] = [
          '',
          input.require ? Validators.required : [],
        ];
      });
    }
    return this.fb.group(g);
  }

  private ensureControls(group: FormGroup, def: DynamicDefinition) {
    if (!def) return;

    if (def.groups && def.groups.length) {
      def.groups.forEach((gr) => {
        let nested = group.get(gr.groupName) as FormGroup | null;
        if (!nested) {
          nested = this.fb.group({});
          group.addControl(gr.groupName, nested);
        }
        gr.inputs.forEach((input) => {
          if (!nested!.get(input.formControlName)) {
            nested!.addControl(
              input.formControlName,
              this.fb.control('', input.require ? Validators.required : []),
            );
          }
        });
      });
    } else if (def.inputs) {
      def.inputs.forEach((input) => {
        if (!group.get(input.formControlName)) {
          group.addControl(
            input.formControlName,
            this.fb.control('', input.require ? Validators.required : []),
          );
        }
      });
    }
  }
}
